import type { ProgramProgressCell } from '../types/domain';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import {
  cellLearningTextForEditor,
  readProgramLearningContentFields,
} from './programLearningContentFields';
import { parseMemoFieldStorageDeep } from './programCellDisplayText';
import {
  resolveProgramContentSlideOwnerCandidates,
  resolveProgramContentSlideOwnerCandidatesForIndicator,
  resolveProgramPresentationLearningTextForIndicator,
} from './programContentSlideOwner';
import type { ProgramLearningContentRowLike } from './programLearningContentFields';
import { resolveProgramWorksheetRefIdForIndicator } from './programContentWorksheetOwner';
import type { ProgramProgressRow } from './programProgressRows';

export function getSyncMirrorRepIndicatorCell(
  annualKey: string,
  syncMirrorView: boolean,
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>
): ProgramProgressCell | undefined {
  if (!syncMirrorView) return undefined;
  return syncMirrorIndicatorCellsByAnnualKey[annualKey];
}

/** 동기화 미러: 대표 scoped 행이 있으면 인디케이터·presence 조회의 canonical 소스로 사용 */
export function resolveSyncMirrorLearningTextForIndicators(
  annualKey: string,
  syncMirrorView: boolean,
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>,
  draftLearningText: string | null | undefined,
  cellLearningText: string | null | undefined,
  repDraftLearningText?: string | null | undefined
): string {
  const repCell = getSyncMirrorRepIndicatorCell(annualKey, syncMirrorView, syncMirrorIndicatorCellsByAnnualKey);
  if (syncMirrorView && repCell) {
    return resolveProgramPresentationLearningTextForIndicator(
      repDraftLearningText ?? draftLearningText,
      cellLearningTextForEditor(repCell),
      cellLearningText
    );
  }
  return resolveProgramPresentationLearningTextForIndicator(
    draftLearningText,
    cellLearningText,
    repCell ? cellLearningTextForEditor(repCell) : null
  );
}

function resolveWorksheetRefIdFromSources(
  ...sources: Array<string | ProgramProgressCell | ProgramLearningContentRowLike | null | undefined>
): string {
  return resolveProgramWorksheetRefIdForIndicator(...sources);
}

/** 동기화 미러: 대표 편제 annualCellKey에 저장된 localStorage 초안 조회 */
export function resolveWorksheetDraftStorageRowKey(
  annualKey: string,
  syncMirrorView: boolean,
  syncMirrorRepAnnualCellKeyByMirrorKey: Record<string, string>
): string {
  const key = annualKey.trim();
  if (syncMirrorView) {
    const repKey = syncMirrorRepAnnualCellKeyByMirrorKey[key]?.trim();
    if (repKey) return repKey;
  }
  return key;
}

export function resolveSyncMirrorWorksheetRefIdForIndicator(
  annualKey: string,
  syncMirrorView: boolean,
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>,
  draftLearningText: string | null | undefined,
  cellLearningText: string | null | undefined,
  repDraftLearningText?: string | null | undefined,
  programCell?: ProgramProgressCell | null
): string {
  const repCell = getSyncMirrorRepIndicatorCell(annualKey, syncMirrorView, syncMirrorIndicatorCellsByAnnualKey);
  if (syncMirrorView && repCell) {
    return resolveWorksheetRefIdFromSources(
      repCell,
      programCell,
      repDraftLearningText,
      draftLearningText,
      cellLearningTextForEditor(repCell),
      cellLearningText
    );
  }
  return resolveWorksheetRefIdFromSources(
    programCell,
    repCell,
    draftLearningText,
    cellLearningText,
    repCell ? cellLearningTextForEditor(repCell) : null
  );
}

export function syncMirrorLearningTextHasMemoNote(
  annualKey: string,
  syncMirrorView: boolean,
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>,
  localLearningText: string | null | undefined,
  cellLearningText?: string | null | undefined
): boolean {
  const local = localLearningText ?? '';
  if (!!parseMemoFieldStorageDeep(local).note.trim()) return true;
  /** 로컬 초안이 DB와 다르면(삭제·편집 직후) 대표 편제 행 메모로 인디케이터를 다시 켜지 않음 */
  if (local.trim() !== (cellLearningText ?? '').trim()) return false;
  const repCell = getSyncMirrorRepIndicatorCell(annualKey, syncMirrorView, syncMirrorIndicatorCellsByAnnualKey);
  if (syncMirrorView && repCell) {
    return !!readProgramLearningContentFields(repCell).note.trim();
  }
  return false;
}

/** 연간진도표와 동일: 동기화 미러 인디케이터는 대표 scoped row owner 기준 */
export function resolveSyncMirrorSlideOwnerCandidatesForIndicator(params: {
  syncMirrorView: boolean;
  annualKey: string;
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>;
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined;
  row: ProgramProgressRow;
  cell: ProgramProgressCell | undefined;
  learningText: string;
}): ProgramContentSlideOwnerRef[] {
  const repCell = getSyncMirrorRepIndicatorCell(
    params.annualKey,
    params.syncMirrorView,
    params.syncMirrorIndicatorCellsByAnnualKey
  );
  if (params.syncMirrorView && repCell?.id?.trim()) {
    const repRow: ProgramProgressRow = { ...params.row, fixedContentId: repCell.id };
    return resolveProgramContentSlideOwnerCandidates(
      params.mode,
      repRow,
      repCell,
      cellLearningTextForEditor(repCell) || params.learningText
    );
  }
  return resolveProgramContentSlideOwnerCandidatesForIndicator({
    mode: params.mode,
    row: params.row,
    cell: params.cell,
    learningText: params.learningText,
    syncMirrorIndicatorCell: repCell,
  });
}
